/**
=========================================================
* Trust Cargo - the customers in one container
=========================================================

* Every customer with orders in the container, each once, with what they have
* in it and what they are charged for it. The columns are in the office's
* order: code, name, VAT name, total CBM, total KG, price, extra, amount,
* phone, address.
*
* Price and extra are typed straight into the table and saved the moment the
* box is left (or Enter is pressed) - one field at a time, so saving a price
* never blanks an extra typed a moment before. Amount is price x total CBM +
* extra, worked out as the numbers are typed rather than after the save, so
* the table never shows a stale amount.
*
* The customer's agreed CBM price, when there is one, shows as a hint in an
* empty price box. It is not a price until somebody types it.
*
* Commission is the other income, kept apart from the amount (shipping): the
* customer's commission % of the final amount of their orders here. Unlike
* the price, the customer's own % counts without being typed - it shows in
* an empty box, and a % typed here is for this container only. Clearing the
* box goes back to the customer's.
*
* Posting (تثبيت): tick customers - or all of them from the box in the
* heading - and post their shipping (price x CBM + extra, in the container's
* currency) or their commission (CNY) in one press, with a note written for
* them. What is posted is kept as it stood: the customer owes it, it is the
* company's income (511 / 512 in the chart), and the price, extra or % it
* came from is locked until the posting is taken back. A confirmed customer is charged on that figure -
* orders edited afterwards no longer move their amount - until the
* confirmation is taken back. Who confirmed it, and when, is kept with it.
*
* Under the table, the container's money: shipping income in the currency
* chosen for this container (it differs from container to container),
* commission income in CNY (the final amount is the goods' yuan value), and
* - for admins, as they come out of the books - the expenses paid out for it
* and the profit. Profit is per currency, income less expenses in that same
* currency: nothing is converted, so nothing is guessed.
*/

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UndoIcon from "@mui/icons-material/Undo";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import { can } from "utils/permissions";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import { api as books, byCurrency, CURRENCIES } from "layouts/accounting/api";
import DialogShell from "layouts/accounting/DialogShell";

const API = "https://trust-cargo.co/anas_backend/api/v2";

export const COLUMNS = [
  // the tick box - which customers a posting is for
  "",
  "Customer code",
  "Customer name",
  "VAT name",
  "Total CBM",
  "Total KG",
  // the customer's cartons in this container - the third measurement
  "Total CTN",
  "Price",
  "Extra",
  "Amount",
  // the commission income, beside the shipping one above
  "Final amount",
  "Com %",
  "Commission",
  "Phone number",
  "Address",
];

/** A number, or null for a box left empty - never NaN. */
const num = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const show = (v, digits = 3) =>
  v === null || v === undefined
    ? "-"
    : Number(v).toLocaleString("en-US", { maximumFractionDigits: digits });

/** price x CBM + extra; nothing when neither has been typed. */
const amountOf = (price, cbm, extra) => {
  const p = num(price);
  const e = num(extra);

  if (p === null && e === null) return null;

  return Math.round(((p || 0) * (num(cbm) || 0) + (e || 0)) * 100) / 100;
};

/** % of the final amount; nothing when there is no % at all. */
export const commissionOf = (rate, final) => {
  const r = num(rate);
  if (r === null) return null;
  return Math.round((num(final) || 0) * r) / 100;
};

/** The final amount is the goods' value in yuan, so commission on it is too. */
export const COMMISSION_CURRENCY = "CNY";

/**
 * Income less expenses, currency by currency. Shipping counts only once
 * its currency is chosen - without one there is no telling what to set it
 * against. Returns {currency: amount}.
 */
export function profitByCurrency({ shipping, shippingCurrency, commission, expenses }) {
  const out = {};
  const add = (cur, v) => {
    if (!cur || !v) return;
    out[cur] = Math.round(((out[cur] || 0) + v) * 100) / 100;
  };

  if (shippingCurrency) add(shippingCurrency, shipping);
  add(COMMISSION_CURRENCY, commission);
  Object.entries(expenses || {}).forEach(([cur, v]) => add(cur, -v));

  return out;
}

/** "1,200.00 CNY · -300.00 USD" - nothing at all is "0". */
const perCurrency = (totals) =>
  Object.keys(totals).length
    ? Object.entries(totals)
        .map(([c, v]) => `${show(v, 2)} ${c}`)
        .join(" · ")
    : "0";

/** A container is loaded - and its CBM can be confirmed - from loading on. */
export const isLoaded = (status) => Boolean(status) && String(status).toLowerCase() !== "planned";

/** The CBM a customer is charged on: the confirmed one, else their orders'. */
export const chargedCbm = (row) =>
  row.confirmed_cbm !== null && row.confirmed_cbm !== undefined ? row.confirmed_cbm : row.total_cbm;

/** Who posted it, when, and the note - for the tick's tooltip. */
const postedBy = (by, at, note) =>
  [by ? `by ${by}` : "", at ? String(at).slice(0, 16) : "", note ? `ملاحظة: ${note}` : ""]
    .filter(Boolean)
    .join(" · ") || "Posted";

const isPosted = (v) => v !== null && v !== undefined;

const POSTING = {
  shipping: { title: "تثبيت الشحن", button: "تثبيت الشحن" },
  commission: { title: "تثبيت العمولات", button: "تثبيت العمولات" },
};

const cell = { fontSize: 13, whiteSpace: "nowrap" };

function ContainerCustomers({ containerId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  // what is being typed, by "customerId.field", until it is saved
  const [drafts, setDrafts] = useState({});
  // the customers ticked, by customer_id
  const [picked, setPicked] = useState(() => new Set());
  // the posting window: {kind, note} while open
  const [posting, setPosting] = useState(null);
  const [postBusy, setPostBusy] = useState(false);
  const [postError, setPostError] = useState("");
  // what the last posting did - how many, and who was skipped and why
  const [notice, setNotice] = useState(null);
  const [rowErrors, setRowErrors] = useState({});
  // the container's expenses by currency - read from the books, so admins only
  const [expenses, setExpenses] = useState(null);
  // a currency that would not save - said beside the box, not over the table
  const [currencyError, setCurrencyError] = useState("");
  // the expenses and profit are the books' - for whoever may keep them
  const isAdmin = can(["containers.expenses", "accounting.view"]);
  // posting shipping / commission to the customers' accounts (تثبيت)
  const mayPost = can("containers.post");

  useEffect(() => {
    let alive = true;

    setData(null);
    setError("");

    fetch(`${API}/containers/${containerId}/customers`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(`Could not load the customers - ${e.message}`);
      });

    return () => {
      alive = false;
    };
  }, [containerId]);

  useEffect(() => {
    if (!isAdmin) return;
    books
      .get("/vouchers", { type: "payment", container_id: containerId })
      .then((rows) => setExpenses(byCurrency(Array.isArray(rows) ? rows : [], (r) => r.amount)))
      .catch(() => setExpenses(null));
  }, [containerId]);

  const shippingCurrency = (data && data.container && data.container.shipping_currency) || "";

  /** Choose what this container's shipping amounts are in. */
  const setShippingCurrency = async (currency) => {
    try {
      await axios.put(`${API}/containers/${containerId}/shipping-currency`, {
        currency: currency || null,
      });
      setData((d) => ({
        ...d,
        container: { ...d.container, shipping_currency: currency || null },
      }));
      setCurrencyError("");
    } catch (e) {
      setCurrencyError(e?.response?.data?.message || "Could not save the currency");
    }
  };

  const rows = (data && data.customers) || [];
  const keyOf = (row, field) => `${row.customer_id}.${field}`;
  const valueOf = (row, field) => {
    const k = keyOf(row, field);
    if (k in drafts) return drafts[k];
    return row[field] === null || row[field] === undefined ? "" : String(row[field]);
  };

  const dropDraft = (k) =>
    setDrafts((d) => {
      const next = { ...d };
      delete next[k];
      return next;
    });

  const patchRow = (customerId, patch) =>
    setData((d) => ({
      ...d,
      customers: d.customers.map((c) => (c.customer_id === customerId ? { ...c, ...patch } : c)),
    }));

  /** Save one field of one customer, if it changed. */
  const save = async (row, field) => {
    const k = keyOf(row, field);
    if (!(k in drafts)) return;

    const typed = String(drafts[k]).trim();
    const value = typed === "" ? null : num(typed);

    if (typed !== "" && value === null) {
      setRowErrors((e) => ({ ...e, [row.customer_id]: `${field} has to be a number` }));
      return;
    }

    if (value === row[field]) {
      dropDraft(k);
      return;
    }

    setRowErrors((e) => ({ ...e, [row.customer_id]: "" }));

    try {
      const res = await axios.put(`${API}/containers/${containerId}/customers/${row.customer_id}`, {
        [field]: value,
      });
      const saved = (res && res.data) || {};

      setData((d) => ({
        ...d,
        customers: d.customers.map((c) =>
          c.customer_id === row.customer_id
            ? {
                ...c,
                price: saved.price !== undefined ? saved.price : c.price,
                extra: saved.extra !== undefined ? saved.extra : c.extra,
                commission_percent:
                  saved.commission_percent !== undefined
                    ? saved.commission_percent
                    : c.commission_percent,
              }
            : c
        ),
      }));
      dropDraft(k);
    } catch (e) {
      setRowErrors((errs) => ({
        ...errs,
        [row.customer_id]: e?.response?.data?.message || "Could not save",
      }));
    }
  };

  const togglePick = (id) =>
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const openPosting = (kind) => {
    setPostError("");
    setPosting({ kind, note: "" });
  };

  /**
   * Post the ticked customers' shipping or commission, with the note - one
   * request, all or none. Customers with nothing to post, or already posted,
   * come back as skipped, each with the reason.
   */
  const post = async () => {
    setPostBusy(true);
    setPostError("");
    try {
      const res = await axios.post(`${API}/containers/${containerId}/post`, {
        kind: posting.kind,
        customer_ids: [...picked],
        note: posting.note,
        user_name: sessionStorage.getItem("user_name"),
      });
      const answer = (res && res.data) || {};
      (answer.posted || []).forEach((r) => patchRow(r.customer_id, r));
      setNotice({
        kind: posting.kind,
        count: (answer.posted || []).length,
        skipped: answer.skipped || [],
      });
      setPicked(new Set());
      setPosting(null);
    } catch (e) {
      setPostError(e?.response?.data?.message || "Could not post");
    }
    setPostBusy(false);
  };

  /** Take one customer's posting back - the price, CBM and % count again. */
  const unpost = async (row, kind) => {
    const what = kind === "shipping" ? "shipping" : "commission";
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Take back the posted ${what} of ${row.code}?`)) return;

    try {
      await axios.delete(
        `${API}/containers/${containerId}/customers/${row.customer_id}/post/${kind}`
      );
      patchRow(
        row.customer_id,
        kind === "shipping"
          ? {
              shipping_posted: null,
              shipping_posted_currency: null,
              shipping_posted_at: null,
              shipping_posted_by: null,
              shipping_note: null,
            }
          : {
              commission_posted: null,
              commission_posted_at: null,
              commission_posted_by: null,
              commission_note: null,
            }
      );
    } catch (e) {
      setRowErrors((errs) => ({
        ...errs,
        [row.customer_id]: e?.response?.data?.message || "Could not take it back",
      }));
    }
  };

  /** A posted amount: the tick, the figure, the note mark, and the way back. */
  const postedCell = (r, kind) => {
    const shipping = kind === "shipping";
    const amount = shipping ? r.shipping_posted : r.commission_posted;
    const currency = shipping ? r.shipping_posted_currency : COMMISSION_CURRENCY;
    const note = shipping ? r.shipping_note : r.commission_note;
    const by = shipping ? r.shipping_posted_by : r.commission_posted_by;
    const at = shipping ? r.shipping_posted_at : r.commission_posted_at;

    return (
      <SuiBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
        <Tooltip title={postedBy(by, at, note)}>
          <SuiBox display="flex" alignItems="center" gap={0.5}>
            <CheckCircleIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
            <SuiTypography
              component="span"
              sx={{ fontSize: 13, fontWeight: 700, color: "#2E7D32" }}
            >
              {`${show(amount, 2)} ${currency || ""}`}
            </SuiTypography>
            {note && <StickyNote2OutlinedIcon sx={{ fontSize: 15, opacity: 0.6 }} />}
          </SuiBox>
        </Tooltip>
        {mayPost && (
          <Tooltip title="فك التثبيت">
            <IconButton
              size="small"
              aria-label={`Undo ${kind} for ${r.code}`}
              onClick={() => unpost(r, kind)}
              sx={{ p: 0.25, opacity: 0.5, "&:hover": { opacity: 1, color: "#D32F2F" } }}
            >
              <UndoIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </SuiBox>
    );
  };

  /** The % that counts for a row: typed here, else the customer's own. */
  const rateOf = (row) => {
    const typed = num(valueOf(row, "commission_percent"));
    return typed !== null ? typed : num(row.default_commission);
  };

  /** Shipping as it counts: what was posted, else what the boxes make it now. */
  const shippingOf = (r) =>
    isPosted(r.shipping_posted)
      ? r.shipping_posted
      : amountOf(valueOf(r, "price"), chargedCbm(r), valueOf(r, "extra"));
  const commissionOfRow = (r) =>
    isPosted(r.commission_posted) ? r.commission_posted : commissionOf(rateOf(r), r.final_amount);

  // the totals follow what is on screen, typed-but-unsaved numbers included
  const totals = rows.reduce(
    (t, r) => ({
      cbm: t.cbm + (num(r.total_cbm) || 0),
      kg: t.kg + (num(r.total_kg) || 0),
      ctn: t.ctn + (num(r.total_ctn) || 0),
      // whether anybody's cartons have been counted at all - a column of
      // dashes should not add up to a confident 0
      counted: t.counted || num(r.total_ctn) !== null,
      extra: t.extra + (num(valueOf(r, "extra")) || 0),
      amount: t.amount + (shippingOf(r) || 0),
      final: t.final + (num(r.final_amount) || 0),
      commission: t.commission + (commissionOfRow(r) || 0),
      shippingPosted: t.shippingPosted + (isPosted(r.shipping_posted) ? 1 : 0),
      commissionPosted: t.commissionPosted + (isPosted(r.commission_posted) ? 1 : 0),
    }),
    {
      cbm: 0,
      kg: 0,
      ctn: 0,
      counted: false,
      extra: 0,
      amount: 0,
      final: 0,
      commission: 0,
      shippingPosted: 0,
      commissionPosted: 0,
    }
  );

  const box = (row, field, extraProps) => (
    <TextField
      size="small"
      type="number"
      value={valueOf(row, field)}
      onChange={(e) => setDrafts((d) => ({ ...d, [keyOf(row, field)]: e.target.value }))}
      onBlur={() => save(row, field)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.target.blur();
      }}
      sx={{ width: 110 }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...extraProps}
    />
  );

  const allPicked = rows.length > 0 && picked.size === rows.length;

  let body;

  if (error) {
    body = (
      <SuiTypography sx={{ fontSize: 14 }} color="error">
        {error}
      </SuiTypography>
    );
  } else if (!data) {
    body = <SuiTypography sx={{ fontSize: 14, opacity: 0.7 }}>Loading customers...</SuiTypography>;
  } else if (rows.length === 0) {
    body = (
      <SuiTypography sx={{ fontSize: 14, opacity: 0.7 }}>
        No orders in this container yet
      </SuiTypography>
    );
  } else {
    body = (
      <SuiBox sx={{ overflowX: "auto" }}>
        <Table sx={{ minWidth: 1200, "& .MuiTableCell-root": { px: 1, py: 0.75 } }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c || "pick"} component="th" align="center">
                  {c === "" ? (
                    <Checkbox
                      size="small"
                      checked={allPicked}
                      indeterminate={picked.size > 0 && !allPicked}
                      onChange={(e) =>
                        setPicked(
                          e.target.checked ? new Set(rows.map((r) => r.customer_id)) : new Set()
                        )
                      }
                      inputProps={{ "aria-label": "Pick all customers" }}
                      sx={{ p: 0.25 }}
                    />
                  ) : (
                    c
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r) => {
              const shippingLocked = isPosted(r.shipping_posted);
              const commissionLocked = isPosted(r.commission_posted);

              return [
                <TableRow key={r.customer_id} selected={picked.has(r.customer_id)}>
                  <TableCell align="center" sx={cell}>
                    <Checkbox
                      size="small"
                      checked={picked.has(r.customer_id)}
                      onChange={() => togglePick(r.customer_id)}
                      inputProps={{ "aria-label": `Pick ${r.code}` }}
                      sx={{ p: 0.25 }}
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                    {r.code || "-"}
                  </TableCell>
                  <TableCell align="center" sx={cell} dir="auto">
                    {r.name || "-"}
                  </TableCell>
                  <TableCell align="center" sx={cell} dir="auto">
                    {r.vat_name || "-"}
                  </TableCell>
                  <TableCell align="center" sx={cell}>
                    {show(r.total_cbm)}
                  </TableCell>
                  <TableCell align="center" sx={cell}>
                    {show(r.total_kg, 2)}
                  </TableCell>
                  {/* "-" until somebody has counted them: no cartons counted
                      is not the same as none */}
                  <TableCell align="center" sx={cell}>
                    {show(r.total_ctn, 0)}
                  </TableCell>
                  <TableCell align="center" sx={cell}>
                    {box(r, "price", {
                      disabled: shippingLocked,
                      placeholder:
                        r.default_price !== null && r.default_price !== undefined
                          ? `agreed ${r.default_price}`
                          : "",
                      inputProps: { min: 0, step: "any", "aria-label": `Price for ${r.code}` },
                    })}
                  </TableCell>
                  <TableCell align="center" sx={cell}>
                    {box(r, "extra", {
                      disabled: shippingLocked,
                      inputProps: { step: "any", "aria-label": `Extra for ${r.code}` },
                    })}
                  </TableCell>
                  <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                    {shippingLocked ? postedCell(r, "shipping") : show(shippingOf(r), 2)}
                  </TableCell>
                  <TableCell align="center" sx={cell}>
                    {show(r.final_amount, 2)}
                  </TableCell>
                  <TableCell align="center" sx={cell}>
                    {box(r, "commission_percent", {
                      disabled: commissionLocked,
                      // the customer's own %, which counts while the box is empty
                      placeholder:
                        r.default_commission !== null && r.default_commission !== undefined
                          ? `${r.default_commission}%`
                          : "",
                      inputProps: {
                        min: 0,
                        max: 100,
                        step: "any",
                        "aria-label": `Commission % for ${r.code}`,
                      },
                      sx: { width: 80 },
                    })}
                  </TableCell>
                  <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                    {commissionLocked ? postedCell(r, "commission") : show(commissionOfRow(r), 2)}
                  </TableCell>
                  <TableCell align="center" sx={cell}>
                    {r.phone || "-"}
                  </TableCell>
                  <TableCell align="center" sx={cell} dir="auto">
                    {r.address || "-"}
                  </TableCell>
                </TableRow>,

                rowErrors[r.customer_id] ? (
                  <TableRow key={`${r.customer_id}-error`}>
                    <TableCell colSpan={COLUMNS.length} sx={{ py: 0.5 }}>
                      <SuiTypography sx={{ fontSize: 12.5 }} color="error">
                        {`${r.code}: ${rowErrors[r.customer_id]}`}
                      </SuiTypography>
                    </TableCell>
                  </TableRow>
                ) : null,
              ];
            })}

            <TableRow>
              <TableCell colSpan={4} align="right" sx={{ ...cell, fontWeight: 800 }}>
                Total
              </TableCell>
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {show(totals.cbm)}
              </TableCell>
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {show(totals.kg, 2)}
              </TableCell>
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {/* a total only of what was counted - nothing counted is "-" */}
                {show(totals.counted ? totals.ctn : null, 0)}
              </TableCell>
              <TableCell />
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {show(totals.extra, 2)}
              </TableCell>
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {show(totals.amount, 2)}
              </TableCell>
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {show(totals.final, 2)}
              </TableCell>
              <TableCell />
              <TableCell align="center" sx={{ ...cell, fontWeight: 800 }}>
                {show(totals.commission, 2)}
              </TableCell>
              <TableCell colSpan={2} />
            </TableRow>
          </TableBody>
        </Table>

        {/* the container's two incomes, each to its own account */}
        <SuiBox display="flex" gap={4} flexWrap="wrap" alignItems="center" pt={2} px={1}>
          <SuiBox display="flex" alignItems="center" gap={1}>
            <SuiTypography sx={{ fontSize: 14 }}>
              Shipping income · ايراد شحن:{" "}
              <b>
                {show(totals.amount, 2)}
                {shippingCurrency ? ` ${shippingCurrency}` : ""}
              </b>
            </SuiTypography>
            <TextField
              select
              size="small"
              value={shippingCurrency}
              onChange={(e) => setShippingCurrency(e.target.value)}
              SelectProps={{ native: true }}
              inputProps={{ "aria-label": "Shipping currency" }}
              sx={{ width: 110 }}
            >
              <option value="">Currency?</option>
              {CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </TextField>
            {currencyError && (
              <SuiTypography sx={{ fontSize: 12.5 }} color="error">
                {currencyError}
              </SuiTypography>
            )}
          </SuiBox>
          <SuiTypography sx={{ fontSize: 14 }}>
            Commission income · ايراد عمولة:{" "}
            <b>{`${show(totals.commission, 2)} ${COMMISSION_CURRENCY}`}</b>
          </SuiTypography>
        </SuiBox>

        {/* what it cost, and what is left - admins, from the books */}
        {expenses !== null && (
          <SuiBox display="flex" gap={4} flexWrap="wrap" alignItems="center" pt={1} px={1}>
            <SuiTypography sx={{ fontSize: 14 }}>
              Expenses · المصاريف: <b>{perCurrency(expenses)}</b>
            </SuiTypography>
            <SuiTypography sx={{ fontSize: 14 }}>
              Profit · الربح:{" "}
              <b>
                {perCurrency(
                  profitByCurrency({
                    shipping: totals.amount,
                    shippingCurrency,
                    commission: totals.commission,
                    expenses,
                  })
                )}
              </b>
            </SuiTypography>
            {!shippingCurrency && totals.amount > 0 && (
              <SuiTypography sx={{ fontSize: 12.5, opacity: 0.6 }}>
                choose the shipping currency for shipping to count in the profit
              </SuiTypography>
            )}
          </SuiBox>
        )}
      </SuiBox>
    );
  }

  const postingKind = posting && POSTING[posting.kind];

  return (
    <Card>
      <SuiBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        px={3}
        pt={2.5}
        pb={1.5}
      >
        <SuiTypography variant="h6">Customers in this container</SuiTypography>
        <SuiBox flex={1} />
        {rows.length > 0 && mayPost && (
          <SuiBox display="flex" alignItems="center" gap={1}>
            {["shipping", "commission"].map((kind) => (
              <Button
                key={kind}
                variant="contained"
                color={kind === "shipping" ? "success" : "info"}
                size="small"
                disabled={picked.size === 0}
                onClick={() => openPosting(kind)}
              >
                {`${POSTING[kind].button} (${picked.size})`}
              </Button>
            ))}
          </SuiBox>
        )}
        {rows.length > 0 && (
          <SuiTypography sx={{ fontSize: 13, opacity: 0.7 }} dir="rtl">
            {`${rows.length} زبون · الشحن مثبّت ${totals.shippingPosted} من ${rows.length}` +
              ` · العمولة مثبّتة ${totals.commissionPosted} من ${rows.length}`}
          </SuiTypography>
        )}
      </SuiBox>

      {notice && (
        <SuiBox px={3} pb={1}>
          <Alert
            severity={notice.skipped.length ? "warning" : "success"}
            onClose={() => setNotice(null)}
          >
            {`${POSTING[notice.kind].title}: تم تثبيت ${notice.count}`}
            {notice.skipped.length > 0 &&
              ` · ما تثبّت ${notice.skipped.length}: ${notice.skipped.join("، ")}`}
          </Alert>
        </SuiBox>
      )}

      <SuiBox px={3} pb={3}>
        {body}
      </SuiBox>

      {/* the note written once, kept on every customer of this posting */}
      <DialogShell
        open={Boolean(posting)}
        title={postingKind ? `${postingKind.title} · ${picked.size} زبون` : ""}
        onClose={() => setPosting(null)}
        onSave={post}
        saving={postBusy}
        error={postError}
        saveLabel={postingKind ? postingKind.button : "Save"}
      >
        {posting && (
          <SuiBox dir="rtl">
            <SuiTypography sx={{ fontSize: 13.5, mb: 1.5 }}>
              {posting.kind === "shipping"
                ? `يتثبّت على كل زبون: السعر × CBM + الإضافي${
                    shippingCurrency ? ` بـ ${shippingCurrency}` : " - اختار عملة الشحن أولاً"
                  }`
                : `يتثبّت على كل زبون: نسبة العمولة × Final amount بـ ${COMMISSION_CURRENCY}`}
            </SuiTypography>
            <TextField
              label="ملاحظة"
              fullWidth
              multiline
              minRows={2}
              value={posting.note}
              onChange={(e) => setPosting({ ...posting, note: e.target.value })}
              inputProps={{ "aria-label": "Posting note", maxLength: 255 }}
            />
          </SuiBox>
        )}
      </DialogShell>
    </Card>
  );
}

ContainerCustomers.propTypes = {
  containerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ContainerCustomers;
